import config from '../../../../config';
import { AddTachoInstallerModel } from '../../../domainModels/versions/AddTachoInstallerModel';
import { TachoReleaseInformationModel } from '../../../domainModels/versions/TachoInstallerModel';
import { axiosInstance as axios } from '../../axios/axiosInstance';
import { mapApiTachoInstallerModel } from './mappings/mapApiTachoInstaller';
import { TachoReleaseInformation } from 'tacho-models';
import { UpdateTachoInstallerModel } from '../../../domainModels/versions/UpdateTachoInstallerModel';
import { mapTachoReleaseTypeToApiModel } from '../../../domainModels/versions/TachoReleaseType';
import { TachoReleaseType } from '../../../domainModels/versions/TachoReleaseType';
import { formatToDateOffset } from '../../../helpers/moment';

export const versionsEndpoint = `${config.tachoAPI.url}/TachoReleasesInformation`;

export const getAllInstallers = (): Promise<Array<TachoReleaseInformationModel>> => {
    return axios
        .get<Array<TachoReleaseInformation>>(versionsEndpoint)
        .then((response: { data: Array<TachoReleaseInformation> }) => {
            return response.data.map(item => mapApiTachoInstallerModel(item));
        })
        .catch((err: string) => {
            throw Error(getAllInstallers.name + err);
        });
};

export const getInstallersForLicense = (
    licenseNumber: string,
    licenseVersionId: string,
): Promise<Array<TachoReleaseInformationModel>> => {
    return axios
        .get<Array<TachoReleaseInformation>>(`${versionsEndpoint}/AvailableForLicense`, {
            params: { licenseNumber: licenseNumber, licenseVersionId: licenseVersionId, includeComponents: true },
        })
        .then(response => {
            return response.data.map(item => mapApiTachoInstallerModel(item));
        })
        .catch((error: string) => {
            throw Error(getInstallersForLicense.name + error);
        });
};

export const getLatestVersionForLicense = (licenseNumber: string): Promise<TachoReleaseInformationModel> => {
    return axios
        .get<TachoReleaseInformation>(`${versionsEndpoint}/LatestForLicense`, {
            params: { licenseNumber: licenseNumber },
        })
        .then(response => {
            return mapApiTachoInstallerModel(response.data);
        })
        .catch((error: string) => {
            throw Error(getLatestVersionForLicense.name + error);
        });
};

export const getInstallerByVersion = (installerVersion: string): Promise<TachoReleaseInformationModel> => {
    return axios
        .get<TachoReleaseInformation>(`${versionsEndpoint}/Versions/${installerVersion}`)
        .then(response => {
            return mapApiTachoInstallerModel(response.data);
        })
        .catch((error: string) => {
            throw Error(getInstallerByVersion.name + error);
        });
};

const onUploadProgress = (onProgress: (percent: number, loaded: number, total: number) => void) => (progressEvent: any) => {
    const { loaded, total } = progressEvent;
    let percent = Math.floor((loaded * 100) / total);
    if (percent < 100) {
        console.log(`${loaded} bytes of ${total} bytes. ${percent}%`);
    }

    if (onProgress != null) {
        onProgress(percent, loaded, total);
    }
};

export const addNewInstaller = (
    model: AddTachoInstallerModel,
    onProgress: (percent: number, loaded: number, total: number) => void,
): Promise<any> => {
    const requestOptions = {
        headers: { 'Content-Type': 'multipart/form-data' },
        crossdomain: true,
        timeout: 0,
        onUploadProgress: onUploadProgress(onProgress),
    };
    var bodyFormData = new FormData();
    const isManagerPackage = model.releaseType === TachoReleaseType.ManagerPackage;
    if (isManagerPackage) {
        (model.components ?? []).forEach(component => {
            const componentFile = model.componentFiles?.[component];
            componentFile && bodyFormData.append(`${component}File`, componentFile);
        });
    } else {
        model.file && bodyFormData.append('file', model.file);
    }
    bodyFormData.append('versionMajor', model.versionMajor.toString());
    bodyFormData.append('versionMinor', model.versionMinor.toString());
    bodyFormData.append('versionPatch', model.versionPatch.toString());
    bodyFormData.append('releaseDate', formatToDateOffset(model.releaseDate));
    bodyFormData.append('releaseType', mapTachoReleaseTypeToApiModel(model.releaseType).toString());
    bodyFormData.append('patchNotes', model.patchNotes);
    bodyFormData.append('md5', isManagerPackage ? '' : model.MD5);
    bodyFormData.append('sha256', isManagerPackage ? '' : model.SHA256);
    bodyFormData.append('size', model.size.toString());
    bodyFormData.append('isBeta', (model.isBeta || false).toString());
    bodyFormData.append('variant', model.variant ?? '');

    return axios
        .post(versionsEndpoint, bodyFormData, requestOptions)
        .then((response: any) => {
            return response;
        })
        .catch(err => {
            throw Error(addNewInstaller.name + err);
        });
};

export const updateInstaller = (
    model: UpdateTachoInstallerModel,
    onProgress: (percent: number, loaded: number, total: number) => void,
): Promise<any> => {
    const requestOptions = {
        headers: { 'Content-Type': 'multipart/form-data' },
        crossdomain: true,
        timeout: 0,
        onUploadProgress: onUploadProgress(onProgress),
    };
    var bodyFormData = new FormData();
    model.file && bodyFormData.append('file', model.file);
    bodyFormData.append('versionMajor', model.versionMajor.toString());
    bodyFormData.append('versionMinor', model.versionMinor.toString());
    bodyFormData.append('versionPatch', model.versionPatch.toString());
    bodyFormData.append('releaseType', mapTachoReleaseTypeToApiModel(model.releaseType).toString());
    bodyFormData.append('releaseDate', formatToDateOffset(model.releaseDate));
    bodyFormData.append('patchNotes', model.patchNotes);
    const isManagerPackage = model.releaseType === TachoReleaseType.ManagerPackage;
    bodyFormData.append('md5', isManagerPackage ? '' : model.MD5 ?? '');
    bodyFormData.append('sha256', isManagerPackage ? '' : model.SHA256 ?? '');
    model.size != null && bodyFormData.append('size', model.size.toString());
    bodyFormData.append('isBeta', (model.isBeta || false).toString());
    bodyFormData.append('variant', model.variant ?? '');

    return axios
        .patch<string>(`${versionsEndpoint}/${model.tachoReleaseInformationId}`, bodyFormData, requestOptions)
        .then((response: { data: string }) => {
            return response.data;
        })
        .catch((err: string) => {
            throw Error(updateInstaller.name + err);
        });
};

export const removeInstaller = (id: string): Promise<string> => {
    return axios
        .delete<boolean>(`${versionsEndpoint}/${id}`)
        .then(() => {
            return id;
        })
        .catch((error: string) => {
            throw Error(removeInstaller.name + error);
        });
};

export const removeInstallers = async (ids: Array<string>): Promise<Array<string>> => {
    const removed: Array<string> = [];
    for (const id of ids) {
        removed.push(await removeInstaller(id));
    }

    return removed;
};
